import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProductionOrderEventsSSE, ProductionOrderEvent } from '../../hooks/useProductionOrderEvents';

type ViewMode = 'equipment' | 'orders' | 'clients';

interface GroupedEvents {
  [key: string]: ProductionOrderEvent[];
}

interface ColumnPagination {
  [key: string]: number;
}

interface WeightEntry {
  concepto: string;
  subconcepto: string;
  weightKg: number;
  unit: string;
}

const EVENTS_PER_PAGE = 50;

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  BACKLOG:       { label: 'Backlog',       color: 'bg-gray-100 text-gray-700' },
  PLANNED:       { label: 'Planeada',      color: 'bg-blue-100 text-blue-700' },
  TODAY:         { label: 'Hoy',           color: 'bg-yellow-100 text-yellow-800' },
  IN_PRODUCTION: { label: 'En Producción', color: 'bg-green-100 text-green-700' },
  AUTHORIZED:    { label: 'Autorizada',    color: 'bg-purple-100 text-purple-700' },
  COMPLETED:     { label: 'Completada',    color: 'bg-teal-100 text-teal-700' },
  CLOSED:        { label: 'Cerrada',       color: 'bg-slate-200 text-slate-600' },
};

function parseWeightFromEvent(event: ProductionOrderEvent): WeightEntry | null {
  const text = event.evidenceText;
  if (!text) return null;

  const pesoMatch = text.match(/Peso:\s*(-?[\d.,]+)\s*(\w+)/i);
  if (!pesoMatch) return null;

  const rawValue = pesoMatch[1].replace(',', '.');
  const unit = pesoMatch[2].toLowerCase();
  let weightKg = parseFloat(rawValue);
  if (isNaN(weightKg)) return null;
  if (unit === 'g') weightKg /= 1000;
  else if (unit === 'lb') weightKg *= 0.453592;
  else if (unit === 'oz') weightKg *= 0.0283495;

  const conceptoMatch = text.match(/Concepto:\s*([^|]+)/i);
  let concepto = '';
  let subconcepto = '';

  if (conceptoMatch) {
    const conceptoPart = conceptoMatch[1].trim();
    if (conceptoPart.includes('>')) {
      const parts = conceptoPart.split('>').map(s => s.trim());
      concepto = parts[0];
      subconcepto = parts[1] || '';
    } else {
      concepto = conceptoPart;
    }
  }

  if (!concepto) return null;

  return { concepto, subconcepto, weightKg, unit: pesoMatch[2] };
}

function buildWeightSummary(events: ProductionOrderEvent[]) {
  const map: Record<string, Record<string, number>> = {};

  for (const ev of events) {
    const w = parseWeightFromEvent(ev);
    if (!w) continue;
    if (!map[w.concepto]) map[w.concepto] = {};
    const key = w.subconcepto || '—';
    map[w.concepto][key] = (map[w.concepto][key] || 0) + w.weightKg;
  }

  return map;
}

const WeightSummaryPanel: React.FC<{ events: ProductionOrderEvent[] }> = ({ events }) => {
  const summary = useMemo(() => buildWeightSummary(events), [events]);
  const conceptos = Object.keys(summary);
  if (conceptos.length === 0) return null;

  return (
    <div className="mx-3 mb-3 bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
      <div className="bg-amber-100 px-3 py-1.5 flex items-center gap-1.5">
        <span className="text-xs">⚖️</span>
        <span className="text-xs font-bold text-amber-800 uppercase tracking-wide">Resumen de Pesos</span>
      </div>
      <div className="p-2 space-y-2">
        {conceptos.map(concepto => {
          const subconceptos = summary[concepto];
          const total = Object.values(subconceptos).reduce((a, b) => a + b, 0);
          return (
            <div key={concepto}>
              <div className="flex items-center justify-between text-xs font-semibold text-amber-900 mb-0.5">
                <span className="truncate">{concepto}</span>
                <span className="ml-2 whitespace-nowrap font-bold">{total.toFixed(2)} kg</span>
              </div>
              {Object.entries(subconceptos).map(([sub, kg]) => (
                <div key={sub} className="flex items-center justify-between pl-3 text-xs text-amber-700">
                  <span className="truncate text-gray-600">↳ {sub}</span>
                  <span className="ml-2 whitespace-nowrap">{kg.toFixed(2)} kg</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ status: string | null }> = ({ status }) => {
  if (!status) return null;
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

const Visor360RT: React.FC = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('equipment');
  const [columnPages, setColumnPages] = useState<ColumnPagination>({});
  const [selectedEvent, setSelectedEvent] = useState<ProductionOrderEvent | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showMachineSelector, setShowMachineSelector] = useState(false);
  // null = no manual selection yet -> show every machine, sorted by activity.
  // Once the user checks/unchecks anything it becomes a real Set and drives
  // which columns render (still sorted activity-first).
  const [selectedMachines, setSelectedMachines] = useState<Set<string> | null>(null);

  const [searchText, setSearchText] = useState('');
  const [filterEvidenceType, setFilterEvidenceType] = useState<'ALL' | 'TEXT' | 'IMAGE'>('ALL');
  const [filterEquipment, setFilterEquipment] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterOrder, setFilterOrder] = useState('');
  const [filterOrderInput, setFilterOrderInput] = useState('');
  const [filterOrderOpen, setFilterOrderOpen] = useState(false);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // 50 was too small: a burst of recent no-equipment admin events ("Cambio de
  // fecha planificada", "Autorización de orden de producción" — see
  // production_order_events with equipment_name IS NULL) can fill the entire
  // most-recent-N window, pushing out older-but-real floor activity for
  // machines like FLEXO 3/4/5 even though it exists in the database. 500
  // comfortably covers today's full event volume (222) with headroom; this
  // is a stopgap — as volume keeps growing, this global top-N fetch will
  // eventually need to become date-range-based instead of a flat count.
  const { events, newEventIds, isConnected, isBootstrapping, refetch } = useProductionOrderEventsSSE(500);

  // The "Equipos" board groups by `equipmentName` recorded on each event, so
  // a machine with no recent event never appeared as a column (ROT-154:
  // "no muestra todos los equipos") — it was silently absent rather than
  // shown empty. Fetching the full active-machine catalog once and union-ing
  // it into `columns` below (equipment view only) makes idle machines show
  // up as empty columns instead of not showing up at all.
  const [catalogEquipmentsFull, setCatalogEquipmentsFull] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    const token = localStorage.getItem('token');
    // /api/machines queries a separate, effectively-unused `machines` table —
    // Tablero de Planeación and the rest of the app register/select equipment
    // through /api/catalogs/equipment (`catalog_equipment`), so that's the
    // catalog that actually has entries like FLEXO 2/3/4. Pointing at the
    // wrong endpoint meant this fetch quietly returned nothing relevant, and
    // idle machines kept not showing up even after the ROT-154 fix shipped.
    fetch('/api/catalogs/equipment?limit=500', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => (res.ok ? res.json() : []))
      .then(data => setCatalogEquipmentsFull(
        Array.isArray(data)
          ? data.filter((m: any) => m.id != null && m.name).map((m: any) => ({ id: m.id, name: m.name }))
          : []
      ))
      .catch(() => {});
  }, []);

  // Derived name-only list — unchanged shape/behavior for the two consumers
  // that only ever needed names (catalogNameByNormalized, allEquipmentColumns).
  const catalogEquipments = useMemo(() => catalogEquipmentsFull.map(e => e.name), [catalogEquipmentsFull]);

  // Equipment identity migration Phase 4a: maps a catalog equipment's id to
  // its current name, so groupEvents below can group by the reliable
  // equipment_id (Phases 1-3) instead of free-text matching whenever an
  // event has one. Falls back to the existing normalized-name matching
  // (catalogNameByNormalized, below) only for legacy rows with no
  // equipmentId, or an id that no longer resolves (deleted/deactivated
  // equipment) — read-time normalization becomes the defensive fallback
  // it always should have been, not the primary mechanism.
  const catalogNameById = useMemo(() => {
    const map = new Map<number, string>();
    catalogEquipmentsFull.forEach(e => map.set(e.id, e.name));
    return map;
  }, [catalogEquipmentsFull]);

  const uniqueEquipments = useMemo(() =>
    [...new Set(events.map(e => e.equipmentName).filter(Boolean) as string[])].sort(), [events]);
  const uniqueCustomers = useMemo(() =>
    [...new Set(events.map(e => e.customerName).filter(Boolean) as string[])].sort(), [events]);
  const uniqueOrders = useMemo(() =>
    [...new Set(events.map(e => e.orderNumber).filter(Boolean) as string[])].sort(), [events]);
  const uniqueStatuses = useMemo(() =>
    [...new Set(events.map(e => e.orderStatus).filter(Boolean) as string[])].sort(), [events]);

  const activeFilterCount = [
    searchText, filterEquipment, filterCustomer, filterOrder, filterDateFrom, filterDateTo, filterStatus,
    filterEvidenceType !== 'ALL' ? filterEvidenceType : '',
  ].filter(Boolean).length;

  const filteredEvents = useMemo(() => {
    let result = events;
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      result = result.filter(e =>
        e.orderNumber?.toLowerCase().includes(q) ||
        e.customerName?.toLowerCase().includes(q) ||
        e.description?.toLowerCase().includes(q) ||
        e.equipmentName?.toLowerCase().includes(q) ||
        e.processName?.toLowerCase().includes(q) ||
        e.createdByName?.toLowerCase().includes(q)
      );
    }
    if (filterEvidenceType !== 'ALL') result = result.filter(e => e.evidenceType === filterEvidenceType);
    if (filterEquipment) result = result.filter(e => e.equipmentName === filterEquipment);
    if (filterCustomer) result = result.filter(e => e.customerName === filterCustomer);
    if (filterOrder) result = result.filter(e => e.orderNumber === filterOrder);
    if (filterStatus) result = result.filter(e => e.orderStatus === filterStatus);
    if (filterDateFrom) {
      const from = new Date(filterDateFrom);
      result = result.filter(e => new Date(e.occurredAt) >= from);
    }
    if (filterDateTo) {
      const to = new Date(filterDateTo);
      to.setHours(23, 59, 59, 999);
      result = result.filter(e => new Date(e.occurredAt) <= to);
    }
    return result;
  }, [events, searchText, filterEvidenceType, filterEquipment, filterCustomer, filterOrder, filterDateFrom, filterDateTo, filterStatus]);

  const clearFilters = () => {
    setSearchText(''); setFilterEvidenceType('ALL'); setFilterEquipment('');
    setFilterCustomer(''); setFilterOrder(''); setFilterOrderInput('');
    setFilterDateFrom(''); setFilterDateTo(''); setFilterStatus('');
  };

  // Maps a normalized (trimmed, uppercased) machine name to the catalog's
  // canonical spelling — e.g. "FLEXO 3 " (trailing space, as stored on some
  // older event) and "Flexo 3" both resolve to whatever the catalog actually
  // has registered. Without this, groupEvents below used the raw
  // equipmentName as-is, so the same physical machine logged under slightly
  // different casing/whitespace over time produced multiple separate
  // columns — each with real events, none of them "wrong", just split up.
  const catalogNameByNormalized = useMemo(() => {
    const map = new Map<string, string>();
    catalogEquipments.forEach(name => map.set(name.trim().toUpperCase(), name));
    return map;
  }, [catalogEquipments]);

  const groupEvents = (evts: ProductionOrderEvent[]): GroupedEvents => {
    const grouped: GroupedEvents = {};
    evts.forEach(event => {
      let key: string;
      switch (viewMode) {
        case 'equipment': {
          const idMatch = event.equipmentId != null ? catalogNameById.get(event.equipmentId) : undefined;
          if (idMatch) {
            key = idMatch;
          } else {
            const raw = event.equipmentName?.trim();
            key = raw ? (catalogNameByNormalized.get(raw.toUpperCase()) || raw) : 'Sin Equipo';
          }
          break;
        }
        case 'orders':    key = event.orderNumber   || 'Sin Orden';  break;
        case 'clients':   key = event.customerName  || 'Sin Cliente'; break;
        default:          key = 'Otros';
      }
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(event);
    });
    return grouped;
  };

  const groupedEvents = useMemo(() => filteredEvents.length > 0 ? groupEvents(filteredEvents) : {}, [filteredEvents, viewMode, catalogNameById, catalogNameByNormalized]);
  // Every known machine for the "Equipos" board, sorted so the ones with
  // activity are easier to scan first — this is also the source list for the
  // machine-selector checklist (always shows everything, regardless of the
  // current selection, so the user can bring a hidden machine back).
  const allEquipmentColumns = useMemo(() => {
    const eventColumns = Object.keys(groupedEvents);
    // Normalize before deduping — `equipmentName` on events is free text set at
    // the time each order was worked, so it can differ in case/whitespace from
    // the catalog's registered name for the very same physical machine (same
    // mismatch already handled this way in PlanningBoardView.tsx). Union-ing
    // the raw strings created a SEPARATE empty column for the catalog's exact
    // spelling alongside the real column the events were actually grouped
    // under, splitting one machine's history across two columns and making it
    // look like the "real" one had disappeared.
    const eventColumnsNormalized = new Map(eventColumns.map(c => [c.trim().toUpperCase(), c]));
    const idleCatalogOnly = catalogEquipments.filter(name => !eventColumnsNormalized.has(name.trim().toUpperCase()));
    return [...eventColumns, ...idleCatalogOnly].sort((a, b) => {
      const aHasActivity = (groupedEvents[a]?.length || 0) > 0 ? 0 : 1;
      const bHasActivity = (groupedEvents[b]?.length || 0) > 0 ? 0 : 1;
      if (aHasActivity !== bHasActivity) return aHasActivity - bHasActivity;
      return a.localeCompare(b);
    });
  }, [groupedEvents, catalogEquipments]);

  const columns = useMemo(() => {
    if (viewMode !== 'equipment') return Object.keys(groupedEvents).sort();
    if (selectedMachines === null) return allEquipmentColumns;
    return allEquipmentColumns.filter(c => selectedMachines.has(c));
  }, [groupedEvents, viewMode, allEquipmentColumns, selectedMachines]);

  const toggleMachineSelected = (name: string) => {
    setSelectedMachines(prev => {
      const base = prev === null ? new Set(allEquipmentColumns) : new Set(prev);
      if (base.has(name)) base.delete(name); else base.add(name);
      return base;
    });
  };

  const getVisibleEvents = (column: string): ProductionOrderEvent[] => {
    const allEvents = groupedEvents[column] || [];
    const page = columnPages[column] || 1;
    return allEvents.slice(0, page * EVENTS_PER_PAGE);
  };

  const hasMoreEvents = (column: string): boolean => {
    const allEvents = groupedEvents[column] || [];
    const page = columnPages[column] || 1;
    return allEvents.length > page * EVENTS_PER_PAGE;
  };

  const loadMoreEvents = (column: string) => {
    setColumnPages(prev => ({ ...prev, [column]: (prev[column] || 1) + 1 }));
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const openOrderPreview = (event: ProductionOrderEvent, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (event.productionOrderId) {
      navigate(`/app/production-orders/${event.productionOrderId}/print`);
    }
  };

  const getViewLabel = () => {
    switch (viewMode) {
      case 'equipment': return 'Equipos';
      case 'orders':    return 'Órdenes';
      case 'clients':   return 'Clientes';
    }
  };

  const renderEvidenceLink = (event: ProductionOrderEvent) => {
    if (event.evidenceType === 'IMAGE' && event.evidenceUrl) {
      return (
        <a href={event.evidenceUrl} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 hover:underline mt-1"
          onClick={e => e.stopPropagation()}>
          🖼️ Ver imagen capturada
        </a>
      );
    }
    return null;
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-full mx-auto">

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <span className="text-3xl">📊</span> Visor 360 RT
              </h1>
              <p className="text-gray-500 text-sm mt-1">Monitoreo en tiempo real de eventos de producción</p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button onClick={() => setViewMode('equipment')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${viewMode === 'equipment' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-200'}`}>
                  🔧 Equipos
                </button>
                <button onClick={() => setViewMode('orders')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${viewMode === 'orders' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-200'}`}>
                  📋 Órdenes
                </button>
                <button onClick={() => setViewMode('clients')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${viewMode === 'clients' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-200'}`}>
                  👥 Clientes
                </button>
              </div>

              {viewMode === 'equipment' && (
                <div className="relative">
                  <button onClick={() => setShowMachineSelector(p => !p)}
                    className={`relative px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${showMachineSelector ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    🖥️ Máquinas
                    {selectedMachines !== null && selectedMachines.size < allEquipmentColumns.length && (
                      <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                        {selectedMachines.size}
                      </span>
                    )}
                  </button>
                  {showMachineSelector && (
                    <div className="absolute z-50 mt-1 w-72 bg-white border border-gray-300 rounded-lg shadow-lg p-3 right-0">
                      <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-100">
                        <span className="text-xs font-semibold text-gray-500 uppercase">
                          Máquinas ({allEquipmentColumns.length})
                        </span>
                        <div className="flex gap-3 text-xs">
                          <button className="text-blue-600 hover:underline"
                            onClick={() => setSelectedMachines(new Set(allEquipmentColumns))}>
                            Todas
                          </button>
                          <button className="text-gray-400 hover:underline"
                            onClick={() => setSelectedMachines(new Set())}>
                            Ninguna
                          </button>
                        </div>
                      </div>
                      <div className="max-h-72 overflow-y-auto space-y-0.5">
                        {allEquipmentColumns.map(col => {
                          const checked = selectedMachines === null || selectedMachines.has(col);
                          const count = groupedEvents[col]?.length || 0;
                          return (
                            <label key={col}
                              className="flex items-center gap-2 text-sm px-1.5 py-1 rounded hover:bg-gray-50 cursor-pointer">
                              <input type="checkbox" checked={checked} onChange={() => toggleMachineSelected(col)}
                                className="rounded border-gray-300" />
                              <span className="flex-1 truncate text-gray-800">{col}</span>
                              <span className={`text-xs font-medium ${count > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                {count}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button onClick={() => setShowFilters(p => !p)}
                className={`relative px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${showFilters ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                🔍 Filtros
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></span>
                <span className="text-sm text-gray-600">{isConnected ? '🟢 En vivo' : '⏸️ Desconectado'}</span>
              </div>
              <button onClick={() => refetch()} className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors" title="Actualizar">🔄</button>
            </div>
          </div>

          {/* Filter panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                <div className="sm:col-span-2 lg:col-span-1">
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Búsqueda general</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                    placeholder="Orden, cliente, descripción, equipo..."
                    value={searchText} onChange={e => setSearchText(e.target.value)} />
                </div>

                {/* Order number combobox */}
                <div className="relative">
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Número de Orden</label>
                  <div className="relative">
                    <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 pr-7"
                      placeholder="Buscar número de orden..."
                      value={filterOrderInput}
                      onChange={e => { setFilterOrderInput(e.target.value); setFilterOrder(''); setFilterOrderOpen(true); }}
                      onFocus={() => setFilterOrderOpen(true)}
                      onBlur={() => setTimeout(() => setFilterOrderOpen(false), 150)} />
                    {filterOrderInput && (
                      <button className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                        onMouseDown={() => { setFilterOrderInput(''); setFilterOrder(''); }}>✕</button>
                    )}
                  </div>
                  {filterOrderOpen && (
                    <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-0.5 max-h-48 overflow-y-auto">
                      {!filterOrderInput && (
                        <div className="px-3 py-2 text-sm text-gray-500 hover:bg-blue-50 cursor-pointer"
                          onMouseDown={() => { setFilterOrder(''); setFilterOrderInput(''); setFilterOrderOpen(false); }}>
                          Todas las órdenes
                        </div>
                      )}
                      {uniqueOrders.filter(o => !filterOrderInput || o.toLowerCase().includes(filterOrderInput.toLowerCase())).map(o => (
                        <div key={o}
                          className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 ${filterOrder === o ? 'bg-blue-100 font-semibold text-blue-700' : 'text-gray-800'}`}
                          onMouseDown={() => { setFilterOrder(o); setFilterOrderInput(o); setFilterOrderOpen(false); }}>
                          {o}
                        </div>
                      ))}
                      {uniqueOrders.filter(o => !filterOrderInput || o.toLowerCase().includes(filterOrderInput.toLowerCase())).length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-400 italic">Sin resultados</div>
                      )}
                    </div>
                  )}
                  {filterOrder && (
                    <div className="mt-1 flex items-center gap-1">
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium">{filterOrder}</span>
                      <button className="text-xs text-gray-400 hover:text-red-500" onClick={() => { setFilterOrder(''); setFilterOrderInput(''); }}>✕</button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Equipo</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 bg-white"
                    value={filterEquipment} onChange={e => setFilterEquipment(e.target.value)}>
                    <option value="">Todos los equipos</option>
                    {uniqueEquipments.map(eq => <option key={eq} value={eq}>{eq}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Cliente</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 bg-white"
                    value={filterCustomer} onChange={e => setFilterCustomer(e.target.value)}>
                    <option value="">Todos los clientes</option>
                    {uniqueCustomers.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Estatus</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 bg-white"
                    value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                    <option value="">Todos los estatus</option>
                    {uniqueStatuses.map(s => {
                      const cfg = STATUS_CONFIG[s] || { label: s, color: '' };
                      return <option key={s} value={s}>{cfg.label}</option>;
                    })}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Tipo de Evidencia</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 bg-white"
                    value={filterEvidenceType} onChange={e => setFilterEvidenceType(e.target.value as any)}>
                    <option value="ALL">Todos</option>
                    <option value="TEXT">📝 Texto</option>
                    <option value="IMAGE">📷 Imagen</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Fecha desde</label>
                  <input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                    value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Fecha hasta</label>
                  <input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                    value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
                </div>
              </div>

              {activeFilterCount > 0 && (
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    Mostrando <strong>{filteredEvents.length}</strong> de <strong>{events.length}</strong> eventos
                  </span>
                  <button onClick={clearFilters} className="text-sm text-red-600 hover:text-red-800 font-medium flex items-center gap-1">
                    ✕ Limpiar filtros
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-blue-600">{filteredEvents.length}</div>
            <div className="text-sm text-gray-500">{activeFilterCount > 0 ? 'Eventos filtrados' : 'Total Eventos'}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-green-600">{columns.length}</div>
            <div className="text-sm text-gray-500">{getViewLabel()}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-purple-600">
              {filteredEvents.filter(e => e.evidenceType === 'IMAGE').length}
            </div>
            <div className="text-sm text-gray-500">Con Imagen</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-orange-600">
              {newEventIds.size > 0 ? `+${newEventIds.size}` : '0'}
            </div>
            <div className="text-sm text-gray-500">Nuevos</div>
          </div>
        </div>

        {/* Main board */}
        {isBootstrapping ? (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <div className="inline-flex min-w-full">
                {[1, 2, 3].map((col) => (
                  <div key={col} className="flex-shrink-0 w-80 border-r border-gray-200 last:border-r-0">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
                      <div className="h-6 w-32 bg-blue-400/50 rounded animate-pulse"></div>
                      <div className="h-4 w-24 bg-blue-400/30 rounded animate-pulse mt-2"></div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {[1, 2, 3, 4].map((item) => (
                        <div key={item} className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse"></div>
                            <div className="flex-1">
                              <div className="h-4 w-20 bg-gray-200 rounded animate-pulse mb-2"></div>
                              <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                              <div className="h-3 w-32 bg-gray-100 rounded animate-pulse mt-2"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : columns.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-6xl mb-4">{activeFilterCount > 0 ? '🔍' : '📭'}</div>
            <h3 className="text-xl font-medium text-gray-600">
              {activeFilterCount > 0 ? 'Sin resultados para los filtros aplicados' : 'No hay eventos registrados'}
            </h3>
            <p className="text-gray-400 mt-2">
              {activeFilterCount > 0
                ? <button onClick={clearFilters} className="text-blue-600 hover:underline">Limpiar filtros</button>
                : 'Los eventos aparecerán aquí cuando se generen en el sistema'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <div className="inline-flex min-w-full">
                {columns.map(column => {
                  const visibleEvents = getVisibleEvents(column);
                  const allColEvents = groupedEvents[column] || [];
                  const totalEvents = allColEvents.length;
                  const hasMore = hasMoreEvents(column);
                  const colStatus = allColEvents[0]?.orderStatus || null;

                  return (
                    <div key={column} className="flex-shrink-0 w-80 border-r border-gray-200 last:border-r-0">
                      {/* Column header */}
                      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sticky top-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-bold text-lg truncate flex-1" title={column}>
                            {viewMode === 'equipment' && '🔧 '}
                            {viewMode === 'orders' && '📋 '}
                            {viewMode === 'clients' && '👤 '}
                            {column}
                          </h3>
                          {viewMode === 'orders' && colStatus && (
                            <StatusBadge status={colStatus} />
                          )}
                        </div>
                        <div className="text-blue-100 text-sm">
                          {visibleEvents.length} de {totalEvents} eventos
                        </div>
                      </div>

                      {/* Weight summary — only in Órdenes view */}
                      {viewMode === 'orders' && (
                        <WeightSummaryPanel events={allColEvents} />
                      )}

                      <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                        {visibleEvents.map(event => (
                          <div
                            key={event.id}
                            onClick={() => setSelectedEvent(event)}
                            className={`p-4 hover:bg-gray-50 transition-all duration-500 cursor-pointer ${
                              newEventIds.has(event.id) ? 'animate-pulse bg-green-50 border-l-4 border-green-500' : ''
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                event.evidenceType === 'IMAGE' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'
                              }`}>
                                {event.evidenceType === 'IMAGE' ? '📷' : '📝'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap mb-1">
                                  {event.orderNumber && viewMode !== 'orders' && (
                                    <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-0.5 rounded">
                                      {event.orderNumber}
                                    </span>
                                  )}
                                  {viewMode !== 'orders' && event.orderStatus && (
                                    <StatusBadge status={event.orderStatus} />
                                  )}
                                </div>
                                <p className="text-sm text-gray-800 font-medium line-clamp-2">
                                  {event.description}
                                </p>
                                {event.processName && (
                                  <p className="text-xs text-gray-500 mt-0.5 truncate">🏭 {event.processName}</p>
                                )}
                                {renderEvidenceLink(event)}
                                {event.productionOrderId && (
                                  <button
                                    onClick={e => openOrderPreview(event, e)}
                                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1"
                                  >
                                    📋 Ver orden
                                  </button>
                                )}
                                <div className="flex items-center gap-2 mt-2">
                                  <span className="text-xs text-gray-400">🕐 {formatDateTime(event.occurredAt)}</span>
                                  {event.createdByName && (
                                    <span className="text-xs text-gray-400">• 👤 {event.createdByName}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}

                        {hasMore && (
                          <button
                            onClick={() => loadMoreEvents(column)}
                            className="w-full p-4 text-center text-blue-600 hover:bg-blue-50 transition-colors font-medium text-sm"
                          >
                            Cargar más eventos ({totalEvents - visibleEvents.length} restantes)
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {isConnected && (
          <div className="fixed bottom-6 right-6 bg-green-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
            EN VIVO
          </div>
        )}

        {/* Event Detail Modal */}
        {selectedEvent && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedEvent(null)}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    {selectedEvent.evidenceType === 'IMAGE' ? '📷' : '📝'} Detalle del Evento
                  </h3>
                  <button onClick={() => setSelectedEvent(null)}
                    className="text-white hover:bg-white/20 rounded-full p-1 transition-colors">✕</button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {selectedEvent.orderNumber && (
                  <div className="flex items-center gap-3">
                    <div>
                      <label className="text-xs text-gray-500 uppercase font-medium">Orden de Producción</label>
                      <p className="text-lg font-bold text-blue-600">{selectedEvent.orderNumber}</p>
                    </div>
                    {selectedEvent.orderStatus && (
                      <div className="mt-3">
                        <StatusBadge status={selectedEvent.orderStatus} />
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="text-xs text-gray-500 uppercase font-medium">Descripción</label>
                  <p className="text-gray-800 mt-1">{selectedEvent.description}</p>
                </div>

                {selectedEvent.equipmentName && (
                  <div>
                    <label className="text-xs text-gray-500 uppercase font-medium">Equipo</label>
                    <p className="text-gray-800 mt-1">🔧 {selectedEvent.equipmentName}</p>
                  </div>
                )}

                {selectedEvent.customerName && (
                  <div>
                    <label className="text-xs text-gray-500 uppercase font-medium">Cliente</label>
                    <p className="text-gray-800 mt-1">👤 {selectedEvent.customerName}</p>
                  </div>
                )}

                {selectedEvent.processName && (
                  <div>
                    <label className="text-xs text-gray-500 uppercase font-medium">Proceso</label>
                    <p className="text-gray-800 mt-1">🏭 {selectedEvent.processName}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 uppercase font-medium">Fecha y Hora</label>
                    <p className="text-gray-800 mt-1">🕐 {new Date(selectedEvent.occurredAt).toLocaleString('es-MX')}</p>
                  </div>
                  {selectedEvent.createdByName && (
                    <div>
                      <label className="text-xs text-gray-500 uppercase font-medium">Registrado por</label>
                      <p className="text-gray-800 mt-1">👤 {selectedEvent.createdByName}</p>
                    </div>
                  )}
                </div>

                {/* Weight breakdown from evidenceText */}
                {(() => {
                  const w = parseWeightFromEvent(selectedEvent);
                  if (!w) return null;
                  return (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <label className="text-xs text-amber-700 uppercase font-bold">⚖️ Registro de Peso</label>
                      <div className="mt-1 space-y-0.5 text-sm">
                        {w.concepto && <div><span className="text-gray-500">Concepto:</span> <span className="font-semibold">{w.concepto}</span></div>}
                        {w.subconcepto && <div><span className="text-gray-500">Subconcepto:</span> <span className="font-semibold">{w.subconcepto}</span></div>}
                        <div><span className="text-gray-500">Peso:</span> <span className="font-bold text-amber-800">{w.weightKg.toFixed(3)} kg</span></div>
                      </div>
                    </div>
                  );
                })()}

                {selectedEvent.evidenceType === 'IMAGE' && selectedEvent.evidenceUrl && (
                  <div>
                    <label className="text-xs text-gray-500 uppercase font-medium">Evidencia (Imagen)</label>
                    <a href={selectedEvent.evidenceUrl} target="_blank" rel="noopener noreferrer"
                      className="block mt-2 text-blue-600 hover:underline">
                      🖼️ Ver imagen completa
                    </a>
                  </div>
                )}

                {selectedEvent.evidenceType === 'TEXT' && selectedEvent.evidenceText &&
                  !selectedEvent.evidenceText.startsWith('/app/') && (
                  <div>
                    <label className="text-xs text-gray-500 uppercase font-medium">Texto de evidencia</label>
                    <p className="text-gray-600 mt-1 italic">"{selectedEvent.evidenceText}"</p>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center rounded-b-lg">
                <button
                  onClick={() => openOrderPreview(selectedEvent)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm"
                >
                  📋 Ver hoja de orden
                </button>
                <button onClick={() => setSelectedEvent(null)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-800 font-medium transition-colors">
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Visor360RT;
